import { useState } from "react";
import {
  Dialog,
  DialogContent,
  IconButton,
  Box,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";

interface ImageDialogProps {
  open: boolean;
  imageUrl: string;
  onClose: () => void;
  altText?: string;
}

export const ImageDialog = ({
  open,
  imageUrl,
  onClose,
  altText = "Enlarged image",
}: ImageDialogProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [imageError, setImageError] = useState(false);

  const handleClose = () => {
    setImageError(false);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={false}
      slotProps={{
        paper: {
          sx: {
            maxHeight: "90vh",
            margin: isMobile ? 0 : "2vh auto",
            width: isMobile ? "100vw" : "90vw",
            maxWidth: isMobile ? "100vw" : "1200px",
            position: "relative",
            bgcolor: "white",
            pt: 5,
          },
        },
      }}
      sx={{
        zIndex: 1500,
      }}
    >
      <IconButton
        onClick={handleClose}
        sx={{
          position: "absolute",
          right: 8,
          top: 8,
          color: "black",
          backgroundColor: "rgba(0, 0, 0, 0.1)",
          zIndex: 1,
          "&:hover": {
            backgroundColor: "rgba(0, 0, 0, 0.2)",
          },
        }}
      >
        <CloseIcon />
      </IconButton>
      <DialogContent
        sx={{
          p: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "400px",
          bgcolor: "white",
        }}
      >
        {imageError ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 2,
              p: 4,
            }}
          >
            <ErrorOutlineIcon sx={{ fontSize: 60, color: "error.main" }} />
            <Typography variant="h6" sx={{ color: "text.primary" }}>
              Failed to load image
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              The image could not be loaded. Please try again.
            </Typography>
          </Box>
        ) : (
          <img
            src={imageUrl}
            alt={altText}
            style={{
              maxWidth: "100%",
              maxHeight: "90vh",
              objectFit: "contain",
            }}
            onError={() => setImageError(true)}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};
